/**
 * Canonical Role Slug Sets
 *
 * Single source of truth for staff/admin role classification.
 * Importable from both client and server code (no server dependencies).
 *
 * NOTE: These sets are for DB-backed role slugs used in role assignments.
 * They cover BOTH slug conventions: 'admin-platform' (legacy JWT metadata)
 * and 'platform-admin' (DB-seeded slug). Platform admin status can also
 * come from user_settings_permissions — always check isPlatformAdmin /
 * is_platform_admin flags in addition to role slugs.
 *
 * Only a SYSTEM role's slug may match these sets (trustedRoleSlug below):
 * every slug here is a RESERVED_ROLE_IDENTIFIER, which custom roles cannot carry.
 */

export const PLATFORM_ADMIN_ROLE_SLUGS: ReadonlySet<string> = new Set([
  'admin-platform', 'platform-admin',
]);

export const STAFF_ROLE_SLUGS = new Set([
  'officer', 'admin', 'supervisor', 'agent',
  ...PLATFORM_ADMIN_ROLE_SLUGS, 'admin-tenant',
  'executive', 'owner', 'training-coordinator',
  'member', 'trainee',  // DB roles mapped from legacy 'officer'
]);

/** Both spellings of the platform-admin slug. Holding one is never platform authority by itself:
 * that is isPlatformAdmin (public.na_is_platform_admin), and route guards trust nothing else. */
export const PLATFORM_ROLE_SLUGS: ReadonlySet<string> = new Set(['platform-admin', 'admin-platform']);

export const ADMIN_ROLE_SLUGS = new Set([
  'admin', ...PLATFORM_ADMIN_ROLE_SLUGS, 'admin-tenant',
  'executive', 'owner',
]);

/**
 * Who may see TENANT-WIDE call analytics: cost totals, raw LLM error text and per-officer
 * rankings (Q-299 P0 #3, Prime ruling 2026-09-25). The admin tier plus supervisors, the same two
 * tiers the database gate admits (public.na_caller_can_read_tenant_analytics, migration 20261312,
 * via public.legacy_role_for_slugs). The database is the enforcement; the UI uses this set only
 * to show a clean "needs a supervisor" state instead of a refused request.
 */
export const TENANT_ANALYTICS_ROLE_SLUGS = new Set([...ADMIN_ROLE_SLUGS, 'supervisor']);

// Mirrors the DB legacy_role_for_slugs tier mapping used by route requiredRoles.
const LEGACY_ROLE_TIERS: ReadonlyArray<readonly [string, readonly string[]]> = [
  ['admin', [...PLATFORM_ADMIN_ROLE_SLUGS, 'admin-tenant', 'owner', 'admin', 'executive']],
  ['supervisor', ['supervisor']],
  ['officer', ['officer', 'agent', 'member', 'trainee', 'training-coordinator']],
  ['citizen', ['citizen']],
  ['guest', ['guest']],
];

export function legacyTierForSlug(slug: string): string | null {
  for (const [tier, slugs] of LEGACY_ROLE_TIERS) if (slugs.includes(slug)) return tier;
  return null;
}

/**
 * Reserved role identifiers (Q-402). A role's slug or name is never authority unless the role
 * is a SYSTEM role: every slug any guard above trusts is reserved, so a custom (non-system) role
 * can neither be created nor renamed to one, by slug or by name. Built from the trusted sets so
 * the list cannot drift from them, plus the aliases and the seeded system roles' display names.
 *
 * The database enforces the same list: public.na_role_identifier_is_reserved(text) behind the
 * CHECK roles_reserved_identifier_system_only (migration 20261371). Pinned against it by
 * lib/auth/reserved-role-identifiers.test.ts.
 */
export const RESERVED_ROLE_IDENTIFIERS: ReadonlySet<string> = new Set([
  ...STAFF_ROLE_SLUGS,
  ...ADMIN_ROLE_SLUGS,
  ...TENANT_ANALYTICS_ROLE_SLUGS,
  ...LEGACY_ROLE_TIERS.flatMap(([, slugs]) => slugs),
  'tenant-admin', 'system', 'superadmin', 'billing', 'developer',
  // Seeded system roles' display names, normalised ('Administrator', 'Billing Manager', ...).
  'administrator', 'platform-administrator', 'billing-manager',
]);

export const ROLE_IDENTIFIER_RESERVED = 'ROLE_IDENTIFIER_RESERVED';

/**
 * The canonical role slug every role write route accepts: lowercase ASCII words joined by single '-'.
 * The database enforces it on every NON-system role (CHECK roles_slug_canonical_unless_system,
 * migration 20261371; the pattern text is pinned equal by reserved-role-identifiers.test.ts).
 */
export const ROLE_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * A ROLE_SLUG_PATTERN slug stem for a display name: accents dropped, every other run of characters
 * outside a-z0-9 collapsed to one '-', 'role' when nothing is left. Writers add their own suffix.
 */
export function roleSlugStem(name: string): string {
  return (
    name
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'role'
  );
}

/**
 * The one normalisation, identical in SQL (public.na_normalize_role_identifier): ASCII letters
 * lowercased (locale-free), every run of whitespace, '_' or '-' collapsed to one '-', and
 * leading/trailing '-' trimmed. ' Platform_Admin ' -> 'platform-admin'. Exact match only:
 * 'admin-tools' stays 'admin-tools' and is not reserved.
 *
 * "Whitespace" is every character JavaScript's \s and String.prototype.trim treat as whitespace,
 * spelled out so the SQL copy is the same class: ASCII \t \n \v \f \r and space, U+00A0, U+1680,
 * U+2000-U+200A, U+2028, U+2029, U+202F, U+205F, U+3000, U+FEFF. So no code that trims or splits on
 * \s downstream can turn an accepted custom identifier into a reserved one (review r3 B1:
 * '\u00a0supervisor\u00a0' was an ordinary custom slug that a consumer's .trim() made 'supervisor').
 * U+200B and U+0085 are not whitespace to JavaScript and are not separators here (pinned vectors).
 */
export function normalizeRoleIdentifier(value: string | null | undefined): string {
  return (value ?? '')
    .replace(/[A-Z]/g, (letter) => letter.toLowerCase())
    .replace(/[\t\n\v\f\r \u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000\ufeff_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function isReservedRoleIdentifier(value: string | null | undefined): boolean {
  return RESERVED_ROLE_IDENTIFIERS.has(normalizeRoleIdentifier(value));
}

/**
 * The slug or name a role writer STORES for a submitted value: String.prototype.trim(), which removes
 * every Unicode space (U+00A0, U+2007, U+202F, U+3000, U+FEFF, ...), not only the ASCII ones the
 * normaliser knows. Every role writer stores this and the write guard below judges this, so the guard
 * and the writer read one value: '\u00a0Owner\u00a0' is stored as 'Owner' and is refused as 'owner'.
 */
export function roleIdentifierAsWritten(value: string | null | undefined): string {
  return (value ?? '').trim();
}

/**
 * The first reserved field of a role being written, or null. Name is checked as well as slug, each as
 * it will be stored (roleIdentifierAsWritten), which is the value the database CHECK judges.
 */
export function findReservedRoleIdentifier(role: {
  slug?: string | null;
  name?: string | null;
}): { field: 'slug' | 'name'; normalized: string } | null {
  for (const field of ['slug', 'name'] as const) {
    const written = roleIdentifierAsWritten(role[field]);
    if (isReservedRoleIdentifier(written)) {
      return { field, normalized: normalizeRoleIdentifier(written) };
    }
  }
  return null;
}

/**
 * The slug a role contributes to authority checks, or null. A system role's slug is trusted. A
 * custom role's slug counts only when it is not reserved, and that is safe because the reserved
 * list is a SUPERSET of every set a guard trusts, so a non-reserved slug matches nothing privileged
 * (pinned by "covers every slug any guard trusts" in lib/auth/reserved-role-identifiers.test.ts:
 * add a trusted slug anywhere without reserving it and that test fails). A role NAME is never
 * returned: names are display text.
 */
export function trustedRoleSlug(role: {
  slug?: string | null;
  is_system_role?: boolean | null;
} | null | undefined): string | null {
  const slug = role?.slug;
  if (!slug) return null;
  if (role?.is_system_role === true) return slug;
  return isReservedRoleIdentifier(slug) ? null : slug;
}
